import React, { useState } from 'react';
import { OptionPresetDto } from '@/services/option-preset';
import { useOptionPresetGroupsWithItems } from '@/services/option-preset/option-preset-group';
import { PresetItemUpdateType, usePresetItemsMutation } from '@/services/option-preset/option-preset-item';
import { OptionPresetItemDto } from '@/types';
import { useAppSelector, useGetActiveProjectId } from '@/utils/hooks';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Dropdown, DropdownButton, Badge } from '@ygtechnologies/rubus-ui';
import { classnames, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import theme from './OptionCard.module.scss';
import { faCheck, faChevronDown, faSwatchbook, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface OptionPresetSelectorProps {
    existingPresetItems: OptionPresetItemDto[];
    optionItemId: number;
    isOptionGroupPublished?: boolean;
    onChange?: () => void;
}

export const OptionPresetSelector = ({
    existingPresetItems,
    optionItemId,
    isOptionGroupPublished = false,
    onChange,
}: OptionPresetSelectorProps) => {
    const projectId = useGetActiveProjectId();
    const isProjectAdmin = useAppSelector((state) => !!state.global.activeProject?.permissions.isAdministrator);
    const { data: presetGroupsWithItems } = useOptionPresetGroupsWithItems(projectId!);
    const presetItemsMutation = usePresetItemsMutation();

    const isSelected = (presetId: number) => existingPresetItems.some((item) => item.optionPresetId === presetId);

    const handleAddPreset = async (preset: OptionPresetDto) => {
        try {
            await presetItemsMutation.mutateAsync({
                presetId: preset.id,
                items: [
                    {
                        optionItemId: optionItemId,
                        updateType: PresetItemUpdateType.Linked,
                    },
                ],
            });
            onChange?.();
        } catch (error) {
            if (error instanceof Response) {
                const message = await error.text();
                Toast.error(message || 'Failed to add style');
            } else {
                Toast.error('Failed to add style');
            }
        }
    };

    const handleRemovePreset = async (preset: OptionPresetDto) => {
        try {
            await presetItemsMutation.mutateAsync({
                presetId: preset.id,
                items: [
                    {
                        optionItemId: optionItemId,
                        updateType: PresetItemUpdateType.Unlinked,
                    },
                ],
            });
            onChange?.();
        } catch (error) {
            if (error instanceof Response) {
                const message = await error.text();
                Toast.error(message || 'Failed to remove style');
            } else {
                Toast.error('Failed to remove style');
            }
        }
    };

    // if (existingPresetItems.length === 0) return null;

    const showAddStylesDropdown = () => {
        const hasGroups = !!presetGroupsWithItems?.length;
        const hasPresets = presetGroupsWithItems?.some((group) => group.presets.length > 0);
        return isProjectAdmin && !isOptionGroupPublished && hasGroups && hasPresets;
    };
    const canEditPreset = () => isProjectAdmin && !isOptionGroupPublished;

    return (
        <div className={theme.optionPresetItemsContainer}>
            {/* Add styles dropdown, shuld be spearate component? */}
            <div>
                {
                    showAddStylesDropdown() && (
                        <DropdownButton label={<Trans>Add styles</Trans>} variant="tertiary" icon={faChevronDown} size="xs">
                            {presetGroupsWithItems?.map((group) => (
                                <Dropdown.Group key={group.id} label={group.name}>
                                    {group.presets.map((preset) => (
                                        <PresetDropdownItem
                                            key={preset.id}
                                            preset={preset}
                                            selected={isSelected(preset.id)}
                                            onAdd={handleAddPreset}
                                            onRemove={handleRemovePreset}
                                            disabled={!canEditPreset()}
                                        />
                                    ))}
                                </Dropdown.Group>
                            ))}
                        </DropdownButton>
                    )
                }

            </div>

            {/* Existing presets, should be separate component? */}
            <div className={theme.optionPresetItems}>
                {existingPresetItems.map((item) => (
                    <Badge
                        key={item.optionPresetItemId}
                        variant={item.isDefault ? 'info' : 'neutral'}
                        appearance='subtle'
                        size="md"
                        className={classnames(theme.optionPresetItemPill, {
                            [theme.optionPresetItemPillLocked]: isOptionGroupPublished,
                        })}
                        onClick={
                            isOptionGroupPublished
                                ? () =>
                                      Toast.error(
                                          t`Cannot change styles. Unpublish the product to make changes.`
                                      )
                                : undefined
                        }
                        iconLeft={faSwatchbook}>
                        <span className={theme.optionPresetPillContent}>
                            <span>{item.optionPresetName}</span>
                            {canEditPreset() && (
                                <button
                                    type="button"
                                    className={theme.removePresetButton}
                                    onClick={() =>
                                        handleRemovePreset({
                                            id: item.optionPresetId,
                                            name: item.optionPresetName,
                                        } as OptionPresetDto)
                                    }
                                    aria-label={`Remove ${item.optionPresetName}`}>
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            )}
                        </span>
                    </Badge>
                ))}
            </div>
        </div>
    );
};

interface PresetDropdownItemProps {
    preset: OptionPresetDto;
    selected: boolean;
    onAdd(preset: OptionPresetDto): Promise<void>;
    onRemove(preset: OptionPresetDto): Promise<void>;
    disabled?: boolean;
}

const PresetDropdownItem: React.FC<PresetDropdownItemProps> = ({ preset, selected, onAdd, onRemove, disabled }) => {
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        if (loading || disabled) return;

        setLoading(true);
        try {
            if (selected) await onRemove(preset);
            else await onAdd(preset);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dropdown.Item
            keepOpen
            onClick={handleClick}
            loading={loading}
            icon={selected ? faCheck : undefined}
            disabled={disabled}>
            {preset.name}
        </Dropdown.Item>
    );
};
